import React, { useState } from 'react';
import { Check, X, Loader2, Tag, AlertCircle } from 'lucide-react';

interface GutscheinInputProps {
  onApply: (code: string, value: number) => Promise<void>;
  onRemove: (code: string) => void;
  appliedCodes: Array<{ code: string; value: number; originalValue: number }>;
  currentTotal: number;
}

const GutscheinInput: React.FC<GutscheinInputProps> = ({
  onApply,
  onRemove,
  appliedCodes,
  currentTotal,
}) => {
  const [code, setCode] = useState('');
  const [validationState, setValidationState] = useState<
    'idle' | 'checking' | 'valid' | 'invalid' | 'already-used' | 'already-applied' | 'applying'
  >('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [validatedValue, setValidatedValue] = useState<number | null>(null);

  const validateCode = async (codeToValidate: string) => {
    if (!codeToValidate.trim()) {
      setValidationState('idle');
      setValidationMessage('');
      setValidatedValue(null);
      return;
    }

    const trimmedCode = codeToValidate.trim().toUpperCase();

    if (appliedCodes.some(c => c.code === trimmedCode)) {
      setValidationState('already-applied');
      setValidationMessage('Dieser Gutschein wurde bereits hinzugefügt');
      setValidatedValue(null);
      return;
    }

    setValidationState('checking');
    setValidationMessage('Gutscheincode wird überprüft...');

    try {
      const { supabase } = await import('@/lib/supabase');

      const { data, error } = await supabase
        .from('Gutschein')
        .select('code, value_eur, is_redeemed')
        .eq('code', trimmedCode)
        .eq('is_redeemed', false)
        .maybeSingle();

      if (error || !data) {
        setValidationState('already-used');
        setValidationMessage('Ungültiger oder bereits eingelöster Gutscheincode');
        setValidatedValue(null);
        return;
      }

      if (data.is_redeemed || !data.value_eur || data.value_eur <= 0) {
        setValidationState('already-used');
        setValidationMessage('Dieser Gutschein wurde bereits vollständig eingelöst');
        setValidatedValue(null);
        return;
      }

      setValidationState('valid');
      setValidatedValue(data.value_eur);
      const deductAmount = Math.min(data.value_eur, currentTotal);
      const remainingAfter = data.value_eur - deductAmount;
      const msg = remainingAfter > 0
        ? `Gutschein gültig – Guthaben: ${data.value_eur.toFixed(2)} € → nach Buchung: ${remainingAfter.toFixed(2)} €`
        : `Gutschein gültig – Guthaben: ${data.value_eur.toFixed(2)} € (wird vollständig eingelöst)`;
      setValidationMessage(msg);
    } catch (err) {
      console.error('Error validating Gutschein:', err);
      setValidationState('invalid');
      setValidationMessage('Fehler bei der Überprüfung des Gutscheincodes');
      setValidatedValue(null);
    }
  };

  const handleApply = async () => {
    if (validationState === 'valid' && validatedValue !== null) {
      setValidationState('applying');
      setValidationMessage('Gutschein wird angewendet...');
      try {
        await onApply(code.trim().toUpperCase(), validatedValue);
        setCode('');
        setValidationState('idle');
        setValidationMessage('');
        setValidatedValue(null);
      } catch (err) {
        setValidationState('invalid');
        setValidationMessage(err instanceof Error ? err.message : 'Fehler beim Anwenden des Gutscheins');
        setValidatedValue(null);
      }
    }
  };

  const handleInputChange = (value: string) => {
    setCode(value);
    const upperValue = value.trim().toUpperCase();

    if (upperValue.length >= 3) {
      validateCode(upperValue);
    } else {
      setValidationState('idle');
      setValidationMessage('');
      setValidatedValue(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Gutscheincode (optional)
        </label>

        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Tag className="h-5 w-5 text-gray-400" />
          </div>

          <input
            type="text"
            value={code}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (validationState === 'valid') {
                  handleApply();
                }
              }
            }}
            disabled={validationState === 'applying'}
            placeholder="z.B. GC-XXXX-XXXX"
            className="w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-sm uppercase disabled:opacity-50 disabled:cursor-not-allowed"
          />

          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {validationState === 'checking' && (
              <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
            )}
            {validationState === 'valid' && (
              <Check className="h-5 w-5 text-green-500" />
            )}
            {(validationState === 'invalid' ||
              validationState === 'already-used' ||
              validationState === 'already-applied') && (
              <X className="h-5 w-5 text-red-500" />
            )}
          </div>
        </div>

        {validationMessage && (
          <div
            className={`mt-2 p-2 rounded-lg flex items-start gap-2 text-sm transition-all ${
              validationState === 'valid'
                ? 'bg-green-50 border border-green-200'
                : validationState === 'checking'
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-red-50 border border-red-200'
            }`}
            style={{
              animation: 'slideInFromBottom 300ms ease-out'
            }}
          >
            {validationState === 'valid' && (
              <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            )}
            {validationState === 'checking' && (
              <Loader2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
            )}
            {(validationState === 'invalid' ||
              validationState === 'already-used' ||
              validationState === 'already-applied') && (
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <span
              className={
                validationState === 'valid'
                  ? 'text-green-800 font-medium'
                  : validationState === 'checking'
                  ? 'text-blue-800'
                  : 'text-red-800'
              }
            >
              {validationMessage}
            </span>
          </div>
        )}

        {(validationState === 'valid' || validationState === 'applying') && validatedValue !== null && (
          <button
            type="button"
            onClick={handleApply}
            disabled={validationState === 'applying'}
            className="mt-3 w-full px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all duration-200 text-sm hover:shadow-md active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              animation: 'slideInFromBottom 300ms ease-out'
            }}
          >
            {validationState === 'applying' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Wird angewendet...
              </>
            ) : (
              <>
                <Tag className="h-4 w-4" />
                Gutschein anwenden
              </>
            )}
          </button>
        )}
      </div>

      {appliedCodes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">
            Angewendete Gutscheine:
          </h4>
          {appliedCodes.map((applied, index) => {
            const costCoveredByPrevious = appliedCodes
              .slice(0, index)
              .reduce((sum, prev) => sum + Math.min(prev.originalValue, Math.max(0, currentTotal - sum)), 0);
            const remainingCostForThis = Math.max(0, currentTotal - costCoveredByPrevious);
            const amountUsedForBooking = Math.min(applied.originalValue, remainingCostForThis);
            const remainingValue = Math.max(0, applied.originalValue - amountUsedForBooking);

            return (
              <div
                key={applied.code}
                className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-3"
                style={{
                  animation: `slideInFromBottom 400ms ease-out ${index * 50}ms both`
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Tag className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="font-mono font-semibold text-sm text-gray-900 truncate">
                        {applied.code}
                      </span>
                    </div>

                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between text-gray-700">
                        <span>Guthaben:</span>
                        <span className="font-medium">{applied.originalValue.toFixed(2)}€</span>
                      </div>

                      {amountUsedForBooking > 0 && (
                        <div className="flex justify-between text-green-700">
                          <span>Rabatt angewendet:</span>
                          <span className="font-semibold">-{amountUsedForBooking.toFixed(2)}€</span>
                        </div>
                      )}

                      {remainingValue > 0 && (
                        <div className="flex justify-between text-blue-700 bg-blue-50 -mx-1 px-1 py-0.5 rounded">
                          <span>Restguthaben:</span>
                          <span className="font-semibold">{remainingValue.toFixed(2)}€</span>
                        </div>
                      )}

                      {remainingValue === 0 && (
                        <div className="flex justify-between text-gray-600 italic">
                          <span>Vollständig eingelöst</span>
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onRemove(applied.code)}
                    className="flex-shrink-0 p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    title="Gutschein entfernen"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GutscheinInput;
